/**
 * ══════════════════════════════════════════════════════════════
 * GOVERNANÇA — ESTADO DO REGISTRO (CAMADA DE PERCEPÇÃO)
 * ══════════════════════════════════════════════════════════════
 *
 * Função única e reutilizável que classifica um registro entre três
 * estados visuais APENAS para fins de comunicação ao usuário:
 *
 *   • 'consolidado' — passou por todas as etapas obrigatórias do fluxo
 *   • 'em_curso'    — ainda em processamento dentro do fluxo
 *   • 'devolvido'   — retornou para ajustes ou foi rejeitado
 *
 * IMPORTANTE:
 *  - Cálculo é 100% client-side, sem novas queries.
 *  - Usa exclusivamente campos JÁ presentes nas queries existentes.
 *  - NÃO afirma integridade criptográfica, validação independente
 *    nem certificação externa. É um rótulo operacional de fluxo.
 * ══════════════════════════════════════════════════════════════
 */

export type RegistryState = 'consolidado' | 'em_curso' | 'devolvido';

export type RegistryDomain =
  | 'contrato'
  | 'boletim'
  | 'medicao'
  | 'execucao';

// Conjuntos canônicos de status já existentes no domínio
const CONTRATO_CONSOLIDADO = new Set(['ativo', 'em_execucao', 'encerrado', 'concluido']);
const CONTRATO_DEVOLVIDO = new Set(['suspenso', 'rescindido', 'cancelado']);

const BOLETIM_CONSOLIDADO = new Set(['aprovado_para_faturamento', 'aprovado', 'validado', 'fechado']);
const BOLETIM_DEVOLVIDO = new Set(['reprovado', 'rejeitado']);

const MEDICAO_CONSOLIDADO = new Set(['aprovada', 'validada', 'consolidada']);
const MEDICAO_DEVOLVIDO = new Set(['glosada', 'reprovada', 'rejeitada']);

const EXECUCAO_CONSOLIDADO = new Set([
  'disponivel_para_medicao',
  'vinculada_a_boletim',
  'validado',
]);
const EXECUCAO_DEVOLVIDO = new Set(['em_apontamento']);

/**
 * Classifica um registro em um dos três estados visuais.
 * Aceita objeto parcial; lê apenas campos já presentes nas queries
 * existentes — nunca dispara fetch.
 */
export function getRegistryState(
  record: Record<string, any> | null | undefined,
  domain: RegistryDomain,
): RegistryState {
  if (!record) return 'em_curso';
  const status = String(record.status ?? '').toLowerCase();

  switch (domain) {
    case 'contrato': {
      if (CONTRATO_DEVOLVIDO.has(status)) return 'devolvido';
      if (record.ativo === true && CONTRATO_CONSOLIDADO.has(status)) return 'consolidado';
      return 'em_curso';
    }

    case 'boletim': {
      if (BOLETIM_DEVOLVIDO.has(status)) return 'devolvido';
      if (BOLETIM_CONSOLIDADO.has(status)) return 'consolidado';
      return 'em_curso';
    }

    case 'medicao': {
      const itemStatus = String(record.status_item ?? status).toLowerCase();
      if (MEDICAO_DEVOLVIDO.has(itemStatus)) return 'devolvido';
      if (MEDICAO_CONSOLIDADO.has(itemStatus)) return 'consolidado';
      return 'em_curso';
    }

    case 'execucao': {
      const qualidade = String(record.qualidade_status ?? '').toLowerCase();
      if (qualidade === 'rejeitada' || EXECUCAO_DEVOLVIDO.has(status)) return 'devolvido';
      if (
        EXECUCAO_CONSOLIDADO.has(status) &&
        qualidade === 'aprovada' &&
        !!record.qualidade_em
      ) {
        return 'consolidado';
      }
      return 'em_curso';
    }
  }
}

export const REGISTRY_STATE_TOOLTIPS: Record<RegistryState, string> = {
  consolidado:
    'Registro que concluiu todas as etapas obrigatórias de governança previstas para este fluxo.',
  em_curso:
    'Registro ainda em processamento dentro do fluxo operacional.',
  devolvido:
    'Registro devolvido para ajustes ou nova avaliação.',
};

export const REGISTRY_STATE_LABELS: Record<RegistryState, string> = {
  consolidado: 'Consolidado',
  em_curso: 'Em curso',
  devolvido: 'Devolvido',
};
